import { useLayoutEffect, useRef, type ReactNode, type RefObject } from "react";
import { DirectiveBuilderChoicePanel } from "./choicePanel";
import { DirectiveBuilderListPanel } from "./listPanel";
import type { DirectiveChipStrategy } from "../../core/chipStrategy";
import type { DirectiveStepProps } from "../../suggestion/logic/stepProps";
import type { DirectiveBuilderStep } from "../../suggestion/storage/session";

export type DirectiveBuilderSurface = "floating" | "chip";

export type DirectiveBuilderStepPanelsProps = {
  surface?: DirectiveBuilderSurface;
  step: DirectiveBuilderStep;
  selectedIndex: number;
  stepProps: Pick<
    DirectiveStepProps,
    | "items"
    | "stepTitle"
    | "choiceSlot"
    | "choiceInitialValue"
    | "onSelectItem"
    | "onChoiceSubmit"
  >;
  choiceStepKey: number;
  emptyMessage?: ReactNode;

  listFilterText?: string;
  onListFilterChange?: (value: string) => void;

  onListHighlightChange?: (index: number) => void;

  listContainerRef?: RefObject<HTMLDivElement | null>;

  listFilterInputRef?: RefObject<HTMLInputElement | null>;
  onListEscape?: () => void;
  listOptionIdPrefix?: string;
  listClassName?: string;
  firstItemRef?: RefObject<HTMLDivElement | null>;

  choiceControlVariant?: "floating" | "chip-edit" | "chip-next";
  choiceFieldId?: string;
  chipNextChoiceContext?: {
    strategy: DirectiveChipStrategy | null;
  };
  choiceInputRef?: RefObject<HTMLInputElement | null>;
  choiceSelectTriggerRef?: RefObject<HTMLButtonElement | null>;
  floatingAllowedTriggerRef?: RefObject<HTMLButtonElement | null>;
};

export const DirectiveBuilderStepPanels = ({
  surface: surfaceProp,
  step,
  selectedIndex,
  stepProps,
  choiceStepKey,
  emptyMessage,
  listFilterText = "",
  onListFilterChange,
  onListHighlightChange,
  listContainerRef,
  listFilterInputRef,
  onListEscape,
  listOptionIdPrefix,
  listClassName,
  firstItemRef,
  choiceControlVariant,
  choiceFieldId,
  chipNextChoiceContext,
  choiceInputRef,
  choiceSelectTriggerRef,
  floatingAllowedTriggerRef,
}: DirectiveBuilderStepPanelsProps): ReactNode => {
  const surface = surfaceProp ?? "floating";
  const isChip = surface === "chip";

  const {
    items,
    stepTitle,
    choiceSlot,
    choiceInitialValue = "",
    onSelectItem,
    onChoiceSubmit,
  } = stepProps;

  const internalListFilterRef = useRef<HTMLInputElement | null>(null);
  const listFilterRef = listFilterInputRef ?? internalListFilterRef;

  useLayoutEffect(() => {
    if (step !== "directive" && step !== "variable") return;
    if (onListFilterChange == null) return;
    const id = requestAnimationFrame(() => {
      listFilterRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(id);
  }, [step, onListFilterChange, listFilterRef]);

  if (step === "choice" && choiceSlot != null) {
    const variant = choiceControlVariant ?? "floating";
    return (
      <DirectiveBuilderChoicePanel
        variant={variant}
        choiceStepKey={choiceStepKey}
        choiceFieldId={choiceFieldId}
        choiceSlot={choiceSlot}
        choiceInitialValue={choiceInitialValue}
        onChoiceSubmit={onChoiceSubmit}
        stepTitle={stepTitle}
        chipNextChoiceContext={chipNextChoiceContext}
        choiceInputRef={choiceInputRef}
        choiceSelectTriggerRef={choiceSelectTriggerRef}
        floatingAllowedTriggerRef={floatingAllowedTriggerRef}
      />
    );
  }

  return (
    <DirectiveBuilderListPanel
      step={step}
      stepTitle={stepTitle}
      isChip={isChip}
      items={items}
      onSelectItem={onSelectItem}
      selectedIndex={selectedIndex}
      onListHighlightChange={onListHighlightChange}
      listOptionIdPrefix={listOptionIdPrefix}
      listClassName={listClassName}
      emptyMessage={emptyMessage}
      listContainerRef={listContainerRef}
      firstItemRef={firstItemRef}
      onListEscape={onListEscape}
      listFilterText={listFilterText}
      onListFilterChange={onListFilterChange}
      listFilterRef={listFilterRef}
    />
  );
};
